/**
 * SOP PDF content blocks (pure, exported for unit tests).
 *
 * The SOP download (SopsPage → downloadSopFile) used to flatten the whole SOP
 * body to a markdown string, which collapsed HTML <table>s to ragged text. To
 * draw a real bordered grid in the PDF we instead model the body as an ORDERED
 * list of blocks — { type: 'text' | 'heading' | 'bullet' | 'table' } — render
 * text/heading/bullet with the existing jsPDF line writer, and tables with
 * renderTable() below.
 *
 * This module is intentionally free of DOM / React / jsPDF-construction deps so
 * it runs under `node --test`. HTML is parsed with string/regex passes (not
 * DOMParser, which is absent in Node), which is enough for the clean, server-
 * rendered markup SOP Galaxy ships. renderTable receives an already-built jsPDF
 * `doc` so it can be exercised with a real document in tests.
 */

import { pdfSafeText } from './pdf-text.js'

const NBSP = String.fromCharCode(160)

// Named HTML entities that actually show up in SOP bodies. Anything else named
// is left as-is (then pdfSafeText maps/strips it); numeric entities decode
// generically. Arrows/dashes decode to ASCII so a cell reads cleanly even
// before pdfSafeText runs.
const NAMED_ENTITIES = {
  amp: '&', lt: '<', gt: '>', quot: '"', apos: "'", nbsp: ' ',
  rarr: '->', larr: '<-', harr: '<->', hellip: '...',
  mdash: '-', ndash: '-', minus: '-', shy: '',
  lsquo: "'", rsquo: "'", sbquo: "'", ldquo: '"', rdquo: '"', bdquo: '"',
  bull: '-', middot: '-', times: 'x', deg: 'deg', copy: '(c)', reg: '(r)', trade: '(tm)',
}

const codePointToText = (cp) => {
  if (!Number.isFinite(cp) || cp < 0 || cp > 0x10ffff) return ''
  try {
    return String.fromCodePoint(cp)
  } catch {
    return ''
  }
}

/** Decode the HTML entities SOP bodies use (named + numeric). */
export function decodeHtmlEntities(input) {
  return String(input ?? '')
    .replace(/&#x([0-9a-f]+);/gi, (_, h) => codePointToText(parseInt(h, 16)))
    .replace(/&#(\d+);/g, (_, d) => codePointToText(parseInt(d, 10)))
    .replace(/&([a-z][a-z0-9]*);/gi, (m, name) => {
      const key = name.toLowerCase()
      return Object.prototype.hasOwnProperty.call(NAMED_ENTITIES, key)
        ? NAMED_ENTITIES[key]
        : m
    })
}

/** Flatten an HTML fragment to text, turning block-level tags into line breaks
 *  and <li> into "- " bullets so lists survive into the PDF. Collapses runs of
 *  spaces but preserves paragraph breaks. */
export function htmlFragmentToText(fragment) {
  if (typeof fragment !== 'string' || !fragment) return ''
  let s = fragment
  s = s.replace(/<\s*br\s*\/?>/gi, '\n')
  s = s.replace(/<\s*li[^>]*>/gi, '\n- ')
  s = s.replace(/<\s*\/\s*li\s*>/gi, '')
  // Other block-level boundaries → newline.
  s = s.replace(/<\s*\/?\s*(p|div|section|article|h[1-6]|ul|ol|table|thead|tbody|tfoot|tr|caption|blockquote)\b[^>]*>/gi, '\n')
  s = s.replace(/<[^>]+>/g, '') // strip any remaining inline tags
  s = decodeHtmlEntities(s)
  return s
    .split(NBSP).join(' ')
    .replace(/[ \t]+/g, ' ')
    .replace(/ *\n */g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim()
}

/** Inline text of a single table cell (no internal line breaks). */
function cellText(html) {
  return htmlFragmentToText(html).replace(/\s*\n\s*/g, ' ').trim()
}

/** Parse one `<table>…</table>` fragment into { headers, rows }. headers is a
 *  string[] when the first row is all <th> cells, else null. */
export function parseHtmlTable(tableHtml) {
  if (typeof tableHtml !== 'string') return { headers: null, rows: [] }
  const trMatches = tableHtml.match(/<tr\b[\s\S]*?<\/tr>/gi) || []
  const parsed = trMatches
    .map((tr) => {
      const cells = []
      const cellRe = /<(t[hd])\b[^>]*>([\s\S]*?)<\/\1>/gi
      let c
      while ((c = cellRe.exec(tr))) {
        cells.push({ header: c[1].toLowerCase() === 'th', text: cellText(c[2]) })
      }
      return cells
    })
    .filter((row) => row.length > 0)
  if (parsed.length === 0) return { headers: null, rows: [] }
  const firstIsHeader = parsed[0].every((c) => c.header)
  const headers = firstIsHeader ? parsed[0].map((c) => c.text) : null
  const bodyRows = (firstIsHeader ? parsed.slice(1) : parsed).map((r) => r.map((c) => c.text))
  return { headers, rows: bodyRows }
}

/** Parse an HTML body into an ORDERED block list, splitting out <table>s so
 *  they render as grids while the surrounding prose stays text (in order). */
export function htmlToBlocks(html) {
  if (typeof html !== 'string' || !html.trim()) return []
  const blocks = []
  const pushText = (frag) => {
    const t = htmlFragmentToText(frag)
    if (t) blocks.push({ type: 'text', text: t })
  }
  const tableRe = /<table\b[\s\S]*?<\/table>/gi
  let last = 0
  let m
  while ((m = tableRe.exec(html))) {
    pushText(html.slice(last, m.index))
    const table = parseHtmlTable(m[0])
    if (table.rows.length || (table.headers && table.headers.length)) {
      blocks.push({ type: 'table', headers: table.headers, rows: table.rows })
    }
    last = m.index + m[0].length
  }
  pushText(html.slice(last))
  return blocks
}

/** Distribute `totalWidth` across columns, weighted by each column's longest
 *  text (clamped) so a short "Area" column stays narrow and a long one gets the
 *  room — then enforce a per-column minimum and renormalise so the row always
 *  spans the full width (borders line up). */
export function computeColumnWidths(weights, totalWidth, { min = 46, maxWeight = 64, minWeight = 6 } = {}) {
  const n = weights.length
  if (n === 0) return []
  if (totalWidth <= 0) return weights.map(() => 0)
  const safeMin = Math.min(min, totalWidth / n)
  const clamped = weights.map((w) => Math.max(minWeight, Math.min(Number(w) || minWeight, maxWeight)))
  const sum = clamped.reduce((a, b) => a + b, 0)
  let widths = clamped.map((w) => (w / sum) * totalWidth)
  widths = widths.map((w) => Math.max(safeMin, w))
  const over = widths.reduce((a, b) => a + b, 0)
  const scale = totalWidth / over
  return widths.map((w) => w * scale)
}

/**
 * Render a { headers, rows } table into an existing jsPDF `doc` as a bordered
 * grid, starting at ctx.top, and return the Y after the table (plus a small
 * gap). Wraps cell text, sizes row height to the tallest cell, fills + bolds
 * the header, and on a page break starts a new page and repeats the header.
 *
 * ctx: { x0, width, top, bottomLimit, topMargin,
 *        fontSize?, border?, headerFill?, headerInk?, bodyInk? }
 */
export function renderTable(doc, table, ctx) {
  const headers = Array.isArray(table?.headers) && table.headers.length ? table.headers : null
  const rows = Array.isArray(table?.rows) ? table.rows : []
  const n = Math.max(headers ? headers.length : 0, ...rows.map((r) => r.length), 1)
  const normalise = (r) => Array.from({ length: n }, (_, i) => String((r && r[i]) ?? ''))
  const headerCells = headers ? normalise(headers) : null
  const bodyCells = rows.map(normalise)

  const {
    x0, width, bottomLimit, topMargin,
    fontSize = 9.5,
    border = [220, 220, 220],
    headerFill = [248, 250, 252],
    headerInk = [20, 20, 20],
    bodyInk = [38, 38, 38],
  } = ctx
  const pad = 5
  const vpad = 4
  const lh = fontSize * 1.3

  // Column widths by longest text per column.
  const weights = []
  for (let c = 0; c < n; c += 1) {
    let w = headerCells ? headerCells[c].length : 0
    for (const r of bodyCells) w = Math.max(w, r[c].length)
    weights.push(w)
  }
  const widths = computeColumnWidths(weights, width)
  const xs = [x0]
  for (let i = 0; i < n; i += 1) xs.push(xs[i] + widths[i])

  let y = ctx.top
  const drawRow = (cells, isHeader) => {
    doc.setFont('helvetica', isHeader ? 'bold' : 'normal')
    doc.setFontSize(fontSize)
    const wrapped = cells.map((cell, c) =>
      doc.splitTextToSize(pdfSafeText(cell), Math.max(1, widths[c] - pad * 2)),
    )
    const rowH = Math.max(1, ...wrapped.map((w) => w.length)) * lh + vpad * 2
    if (y + rowH > bottomLimit) {
      doc.addPage()
      y = topMargin
      if (headerCells && !isHeader) drawRow(headerCells, true)
    }
    const top = y
    const ink = isHeader ? headerInk : bodyInk
    for (let c = 0; c < n; c += 1) {
      if (isHeader) {
        doc.setFillColor(headerFill[0], headerFill[1], headerFill[2])
        doc.rect(xs[c], top, widths[c], rowH, 'F')
      }
      doc.setDrawColor(border[0], border[1], border[2])
      doc.setLineWidth(0.5)
      doc.rect(xs[c], top, widths[c], rowH)
      doc.setTextColor(ink[0], ink[1], ink[2])
      let ty = top + vpad + fontSize
      for (const ln of wrapped[c]) {
        doc.text(ln, xs[c] + pad, ty)
        ty += lh
      }
    }
    y = top + rowH
  }

  if (headerCells) drawRow(headerCells, true)
  for (const r of bodyCells) drawRow(r, false)
  return y + 6
}
